import React, {useEffect} from 'react';

import {Form, Field, FormSpy} from 'react-final-form'
import "react-datepicker/dist/react-datepicker.css";

import {fieldMapApiSlice} from "../../fieldMap/fieldMapApiSlice";
import Spinner from "../../../Spinner";

import {modelFormUpdated} from "../../fieldMap/fieldMapSlice";
import {useAppDispatch, useAppSelector} from "../../../app/hooks";
import {MetSite} from "../../fieldMap/types";
import {v4 as uuidv4} from "uuid"
import {expectedDataAvailabilityBoundaryDateTime} from "../../fieldMap/WindDataHelpers";
import WindDataDatePicker from "../../fieldMap/WindDataDatePicker";
import {InputSelect, InputSelectOption} from "../../common/InputSelect";
import {MetSiteSelect} from "../../fieldMap/MetSiteSelect";
import {DateTime} from "luxon";

interface Values {
    detectionDateTime: Date;
    distanceLimitMeters: number;
    metStationId?: string;
}

const initialValues = {
    detectionDateTime: expectedDataAvailabilityBoundaryDateTime(15),
    distanceLimitMeters: 1000
}

const getDistanceOptions = () => {
    let distanceOptions = [] as InputSelectOption[]

    for(let i = 2; i <= 40; i+=2) {
        let meters = String(i * 100)
        distanceOptions = distanceOptions.concat([{
            value: meters,
            label: meters + " meters"
        }])
    }

    return distanceOptions
}

const ReverseDispersionForm: React.FC = () => {
    // @TODO
    // not sure why I can't just use the convenience hook, useFetchMetSitesQuery...

    const userOrganizationId = useAppSelector(state => state.auth.organizationId) as string

    const { data: fieldMap, isLoading: isFieldMapLoading } = fieldMapApiSlice.endpoints.fetchFieldMap.useQuery(userOrganizationId);

    const selectedMetSiteId = useAppSelector(state => state.fieldMap.selectedMetSiteId) as string
    const selectedDateTime = useAppSelector(state => state.fieldMap.selectedDateTime) as string

    let {data: metTimeSeries = []} = fieldMapApiSlice.endpoints.fetchMetTimeSeries.useQuery({
        date: DateTime.fromISO(selectedDateTime).startOf('day').toISO(),
        metStationIds: [selectedMetSiteId]
    }, {skip: (!selectedMetSiteId) || !selectedDateTime})

    const selectedCoordinate = useAppSelector(state => state.fieldMap.selectedCoordinate)
    const organizationId = useAppSelector(state => state.auth.organizationId)
    const modelResultsStatus = useAppSelector(state => state.model.status)
    const lastModelInstanceId = useAppSelector(state => state.model.last_modelinstance_id)

    const dispatch = useAppDispatch()


    const onSubmit = async (values: Values) => {

        if(!values.metStationId) {
            // @TODO real final-form validation, use `validate` prop on Form
            window.alert("Please select a meteorological station");
            return false;
        }
        if(!selectedCoordinate) {
            // @TODO real final-form validation, use `validate` prop on Form
            window.alert("Please select a detection coordinate");
            return false;
        }

        if(!organizationId) {
            window.alert("Authentication error, please refresh the page")
            return false;
        }

        if(metTimeSeries.length < 1){
            window.alert(
                "Error, no meteorological data available for the selected station and time.\n" +
                "Please select a station and time where meteorological data is available."
            )
            return false;
        }

        const modelinstance_id = uuidv4()
        dispatch(fieldMapApiSlice.endpoints.putModelInstanceRequest.initiate({
            modelinstance_id: modelinstance_id,
            organization_id: organizationId,
            // BABOON
            modeltype_id: "54ad096e-adc8-4d81-b177-45c8826c47ee",
            model_input: {
                detection_datetime: values.detectionDateTime.toISOString(),
                max_distance: values.distanceLimitMeters,
                met_site_id: values.metStationId,
                detection_coordinate: selectedCoordinate
            }
        }))
        dispatch(fieldMapApiSlice.endpoints.fetchModelInstance.initiate(modelinstance_id))
    }

    const [metSites, setMetSites] = React.useState<MetSite[]>([])

    const metStationOptions = metSites.map(s => ({
        value: s.id,
        label: s.letter + " - " + s.label
    })) as InputSelectOption[]

    useEffect(() => {
        if (!fieldMap) {
            return;
        }

        setMetSites(fieldMap.map_json.met_sites);


        dispatch(modelFormUpdated({
            selectedDateTime: initialValues.detectionDateTime.toISOString()
        }))
    }, [fieldMap, dispatch])

    const [lastFailureReference, setLastFailureReference] = React.useState<string>("")

    useEffect(() => {
        if(modelResultsStatus === 'failed' && lastModelInstanceId && lastModelInstanceId !== lastFailureReference) {
            window.alert("An unexpected error occurred.")
            setLastFailureReference(lastModelInstanceId)
        }

    }, [modelResultsStatus])

    return (
        <Form
            onSubmit={onSubmit}
            initialValues={{
                ...initialValues
            }}
            render={({ handleSubmit, submitting, pristine}) => (
                <form onSubmit={handleSubmit}>
                    <FormSpy onChange={state => {
                        dispatch(modelFormUpdated({
                            selectedDateTime: state.values.detectionDateTime.toISOString(),
                            selectedMetSiteId: state.values.metStationId,
                        }))
                    }} />
                    <div className="col-12" style={{fontSize: "small"}}>
                        <div className="row mb-2">
                            <div className="col-12">
                                <label htmlFor="steps_select">
                                    &nbsp;
                                </label>
                            </div>
                            <div className="col-12">

                                <button className="btn w-100 btn-sm btn-primary"
                                        type="submit"
                                        id="run-control"
                                        disabled={submitting || modelResultsStatus === 'loading'}
                                >
                                    {submitting || modelResultsStatus === 'loading'
                                        ? <Spinner />
                                        : "Run" }
                                </button>
                            </div>
                        </div>
                        <hr />
                        <div className="row mb-2">
                            <div className="col-12">
                                <label htmlFor="detectionDateTime">Detection date and time (Pacific)</label>
                            </div>
                            <div className="col-12 datetime-picker-parent__full-width">
                                <Field<Date> name={"detectionDateTime"} offset={15}
                                       component={WindDataDatePicker}
                                       />
                            </div>
                        </div>
                        <div className="row mb-2">
                            <div className="col-12">
                                <label htmlFor="distanceLimitMeters">Total back trajectory distance</label>
                            </div>
                            <div className="col-12">
                                <Field<string> name={"distanceLimitMeters"}
                                               component={InputSelect}
                                               className={"form-control form-control-sm"}
                                >
                                    {getDistanceOptions().map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </Field>
                            </div>
                        </div>
                        <div className="row mb-2">
                            <div className="col-12">
                                <label htmlFor="metStationId">Meteorological station</label>
                            </div>
                            <div className="col-12">
                                {isFieldMapLoading
                                    ? <Spinner/>
                                    : <MetSiteSelect metStationOptions={metStationOptions}/>
                                }
                            </div>
                        </div>
                    </div>
                </form>
            )}
            />
    )
}

export default ReverseDispersionForm;